import { useCallback, useEffect, useReducer, useRef } from "react";
import { useHookCacheContext } from "./HookCacheProvider";
import { serializeKey } from "./keys";
import type { CacheEntry, CacheKey } from "./types";

export interface UseHookCacheQueryArgs<T> {
  key: CacheKey;
  fetcher: () => Promise<T>;
  enabled?: boolean;
}

export interface UseHookCacheQueryResult<T> {
  data: T | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * TanStack-Query-shaped primitive backed by `<HookCacheProvider>`.
 *
 * Returns `{ data, isLoading, error, refetch }`. Subscribes the component to
 * the entry behind `key` so any `setEntry`/`invalidate` re-renders consumers.
 *
 * The hook is intentionally minimal: no stale-while-revalidate, no
 * refetch-on-focus, no auto-eviction. Cache lives as long as the provider
 * is mounted.
 */
export function useHookCacheQuery<T>({
  key,
  fetcher,
  enabled = true,
}: UseHookCacheQueryArgs<T>): UseHookCacheQueryResult<T> {
  const cache = useHookCacheContext();
  const [, forceRender] = useReducer((tick: number) => tick + 1, 0);
  const entryRef = useRef<CacheEntry<T> | null>(null);
  const serialized = enabled ? serializeKey(key) : "";

  // Reset the cached entry ref synchronously when the key changes. Without
  // this the render-phase fast-path below keeps returning the *previous*
  // key's entry — the useEffect that updates the ref only fires post-render
  // and doesn't trigger a re-render of its own, so consumers see stale data
  // for one render after switching keys (visible as a click → flash → new
  // data lag, even on a warm cache).
  const lastSerializedRef = useRef(serialized);
  if (lastSerializedRef.current !== serialized) {
    lastSerializedRef.current = serialized;
    entryRef.current = null;
  }

  if (enabled && !entryRef.current) {
    entryRef.current = cache.registerQuery(key, fetcher);
  }

  useEffect(() => {
    if (!enabled) {
      entryRef.current = null;
      return;
    }
    const entry = cache.registerQuery(key, fetcher);
    entryRef.current = entry;
    entry.subscribers.add(forceRender);
    return () => {
      cache.unregisterSubscriber(key, forceRender);
    };
    // `serialized` covers identity changes to `key`. `fetcher` is expected to
    // be stable for a given (key, args) pair.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache, enabled, serialized]);

  const refetch = useCallback(async () => {
    if (!enabled) return;
    await cache.invalidate(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache, enabled, serialized]);

  if (!enabled) {
    return {
      data: undefined,
      isLoading: false,
      error: null,
      refetch: async () => {},
    };
  }

  const entry = entryRef.current;
  if (!entry) {
    return {
      data: undefined,
      isLoading: true,
      error: null,
      refetch,
    };
  }
  return {
    data: entry.data,
    isLoading: entry.isLoading,
    error: entry.error,
    refetch,
  };
}
